<template>
    <div
        class="advanced-filter-builder mb-md display-flex align-items-start justify-between w-100"
        @keydown.enter="atLeastOneFilterComplete && applyFilters()"
    >
        <div class="chain-container display-flex align-items-center flex-wrap w-100">
            <div class="filter-chain display-flex align-items-start flex-wrap">
                <div
                    v-for="(filter, index) in props.prefillFilters"
                    :class="[
                        'filter-item prefill-filter mb-sm mr-md display-flex align-items-center filter-uneditable',
                    ]"
                >
                    <div class="main-params display-flex align-items-center">
                        <div class="section display-flex align-items-center">
                            <font-awesome-icon icon="fas fa-text" class="mr-xs text-moat-600" />
                            <p>Patent Id</p>
                        </div>
                        <div class="section display-flex align-items-center">
                            <font-awesome-icon icon="fas fa-check" class="mr-xs text-moat-600" />
                            <p>Contains</p>
                        </div>
                        <div class="section display-flex align-items-center">
                            <p>{{ filter.value }}</p>
                        </div>
                        <div class="delete cursor-pointer p-sm" @click="removePrefillFilter(index)">
                            <font-awesome-icon icon="fas fa-trash-can" />
                        </div>
                    </div>
                </div>
                <div
                    v-for="(filter, index) in filterChain"
                    :class="[
                        'filter-item mb-lg mr-md display-flex align-items-center',
                        {
                            'filter-complete': isFilterComplete(filter),
                            'filter-incomplete': !isFilterComplete(filter),
                        },
                    ]"
                    :key="filter.id"
                >
                    <ArmoryOptionToggle
                        v-if="index > 0 || props.prefillFilters.length"
                        class="mr-md"
                        style="
                            border: 1px solid var(--color-stone-400);
                            border-radius: var(--border-radius-lg);
                        "
                        v-model="filter.join"
                        :options="[
                            { label: 'OR', value: 'or' },
                            { label: 'AND', value: 'and' },
                        ]"
                        size="sm"
                        @update:modelValue="(val) => onToggleChange(val, index)"
                    />
                    <div class="main-params display-flex align-items-center">
                        <AdvancedFilterChainDropdown
                            v-model="filter.column"
                            :options="columnOptions"
                            placeholder="Column"
                            type="column"
                            @change="(val) => selectColumn(val, index)"
                            @update:modelValue="updateColumn(filter.column, index)"
                        />
                        <div
                            class="set-operator px-sm"
                            v-if="filter.column && filter.type === 'set'"
                        >
                            <p>IS</p>
                        </div>
                        <AdvancedFilterChainDropdown
                            :class="{ disabled: !filter.column }"
                            v-model="filter.operator"
                            :options="getOperatorsForFilter(filter)"
                            :placeholder="filter.type === 'set' ? 'Value' : 'Operator'"
                            type="operator"
                            @change="(val) => selectOperator(val, index)"
                            @update:modelValue="updateOperator(filter, index)"
                        />
                        <AdvancedFilterChainInput
                            v-if="filter.column && filter.type !== 'set'"
                            :key="`${filter.id}-${filter.column}-${filter.operator}`"
                            :class="{ disabled: !filter.operator }"
                            v-model="filter.value"
                            placeholder="Value"
                            :type="filter.type"
                            :operator="filter.operator"
                            @remove-filter="removeFilter(index)"
                        />
                        <div class="delete cursor-pointer p-sm" @click="removeFilter(index)">
                            <font-awesome-icon icon="fas fa-trash-can" />
                        </div>
                    </div>
                </div>
                <div
                    class="add-filter display-flex align-items-center cursor-pointer p-sm ml-sm"
                    @click="addFilter"
                >
                    <font-awesome-icon :icon="`fas fa-plus`" class="text-moat-600" />
                    <p class="geist-font text-moat-600 bold ml-xs">Add Filter</p>
                </div>
            </div>
        </div>
        <div class="apply-filters">
            <AonButton
                :class="{ disabled: !atLeastOneFilterComplete }"
                style="width: 150px; height: 40px"
                label="Apply Filters"
                @clicked="applyFilters"
            >
            </AonButton>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import AdvancedFilterChainDropdown from './AdvancedFilterChainDropdown.vue'
import AdvancedFilterChainInput from './AdvancedFilterChainInput.vue'

const props = defineProps({
    columnDefs: {
        type: Array,
        required: true,
    },
    prefillFilters: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['apply-filters'])

const filterChain = ref([])

const getOperatorsForFilter = (filter) => {
    if (!filter.column) return []

    const targetColumn = props.columnDefs.find((col) => col.colId === filter.column)
    const options =
        targetColumn?.filterParams.filterOptions || targetColumn?.filterParams.values || []

    return options.map((option) => {
        let label = option.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())

        // Use valueFormatter if available to get proper display labels
        if (targetColumn?.filterParams?.valueFormatter) {
            const formattedValue = targetColumn.filterParams.valueFormatter({ value: option })
            if (formattedValue) {
                label = formattedValue
            }
        }

        return {
            label: label,
            value: option,
        }
    })
}

const columnOptions = computed(() => {
    return props.columnDefs?.map((col) => {
        return {
            label: col.headerName || col.colId,
            value: col.colId,
            type:
                col.filter === 'agTextColumnFilter'
                    ? 'text'
                    : col.filter === 'agNumberColumnFilter'
                      ? 'number'
                      : col.filter === 'agDateColumnFilter'
                        ? 'date'
                        : col.filter === 'agSetColumnFilter'
                          ? 'set'
                          : col.filter === 'agMultiSelectColumnFilter'
                            ? 'multi-select'
                            : 'text',
        }
    })
})

const addFilter = () => {
    filterChain.value.push({
        id: Date.now() + Math.random(), // stable unique key for this filter row
        column: null,
        operator: null,
        value: null,
        type: null,
        join: 'and', // default join with next condition
    })
}

const removeFilter = (index) => {
    filterChain.value.splice(index, 1)
}

const removePrefillFilter = (index) => {
    props.prefillFilters.splice(index, 1)
}

const updateColumn = (colId, index) => {
    filterChain.value[index].column = colId
}

const selectColumn = (agGridColumnn, index) => {
    // Set column type on the correct filter row
    filterChain.value[index].type = agGridColumnn.type

    // Reset operator and value whenever the column changes
    filterChain.value[index].operator = null
    filterChain.value[index].value = null
}

const updateOperator = (filter, index) => {
    filterChain.value[index].operator = filter.operator
    // Clear value when operator changes, except for set filters where operator IS the value
    if (filterChain.value[index].type !== 'set') {
        filterChain.value[index].value = null
    }
}

const selectOperator = (agGridFilter, index) => {
    if (filterChain.value[index].type === 'set') {
        filterChain.value[index].value = agGridFilter.value
    }
}

const onToggleChange = (value, index) => {
    if (filterChain.value[index]) {
        filterChain.value[index].join = value
    }
}

const applyFilters = () => {
    const combinedFilters = [...props.prefillFilters, ...filterChain.value]
    emit('apply-filters', combinedFilters)
}

const isFilterComplete = (filter) => {
    // A filter is considered complete when all required fields have a value.
    if (!filter || !filter.column) {
        return false
    }

    // For set filters, only the column and operator (which serves as the value) are required.
    if (filter.type === 'set') {
        return !!filter.operator
    }

    // For all other filter types, column, operator and value are required.
    const hasOperator =
        filter.operator !== null && filter.operator !== undefined && filter.operator !== ''
    const hasValue = filter.value !== null && filter.value !== undefined && filter.value !== ''

    return hasOperator && hasValue
}

const atLeastOneFilterComplete = computed(() => {
    return filterChain.value.some(isFilterComplete)
})
</script>

<style scoped lang="scss">
.advanced-filter-builder {
    display: flex;
    position: relative;
    top: 0;
    min-height: 64px;

    // .disabled {
    //     opacity: 0.5;
    //     pointer-events: none;
    // }

    .filter-item {
        .main-params {
            border-radius: var(--border-radius-xl);
            padding: 2px;
            border: 2px solid var(--color-warning);

            transition: border 0.3s ease-in-out;
        }

        &.filter-complete {
            .main-params {
                border: 2px solid var(--color-moat-500);
            }
        }
    }

    .prefill-filter {
        .main-params {
            border: 2px solid var(--color-stone-600);

            .section {
                min-height: 40px;
                height: 100%;
                border-top: 1px solid var(--color-stone-400);
                border-bottom: 1px solid var(--color-stone-400);
                border-left: 1px solid var(--color-stone-400);
                padding: 9px 16px !important;
                background: var(--color-stone-0);

                &:first-of-type {
                    border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
                }

                &:last-of-type {
                    border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
                }
            }
        }
    }

    .add-filter {
        p,
        svg {
            transition: color 0.3s ease-in-out;
        }

        &:hover {
            p,
            svg {
                color: var(--color-moat-800) !important;
            }
        }
    }

    .set-operator {
        height: 40px;
        padding-top: 10px;
        border-top: 1px solid var(--color-stone-400);
        border-bottom: 1px solid var(--color-stone-400);
        border-left: 1px solid var(--color-stone-400);
        background: var(--color-stone-0);
        cursor: pointer;
        box-shadow:
            0px 1px 1px -0.5px var(--color-stone-200),
            0px 3px 3px -1.5px var(--color-stone-200);
    }

    .delete {
        height: 40px;
        border: 1px solid var(--color-stone-400);
        border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
        background: var(--color-stone-0);
        cursor: pointer;
        box-shadow:
            0px 1px 1px -0.5px var(--color-stone-200),
            0px 3px 3px -1.5px var(--color-stone-200);

        transition: background 0.2s ease-in-out;

        svg {
            transition: color 0.2s ease-in-out;
        }

        &:hover {
            background: var(--color-stone-200);
            svg {
                color: #e92020 !important;
            }
        }
    }
}
</style>
